import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';

export interface SaveSafeRouteDialogData {
    cameras: any[];
    polylinePoints: Array<{ lat: number; lng: number; checkpointAt?: string; checkpointOffsetMinutes?: number; toleranceTimerMinutes?: number }>;
    selectedCameraIds?: string[];
    selectedColor?: string;
    selectedName?: string;
    selectedDeviationDistanceMeters?: number | null;
    distanceUnit?: string;
    isUpdate?: boolean;
    currentRouteId?: string | null;
}

export interface SaveSafeRouteCameraRef {
    _id: string;
    name: string | null;
}

export interface SaveSafeRouteDialogResult {
    cameraIds: SaveSafeRouteCameraRef[];
    color: string;
    name: string;
    deviationDistanceMeters?: number;
}

@Component({
    selector: 'app-save-safe-route-dialog',
    templateUrl: './save-safe-route-dialog.component.html',
    styleUrls: ['./save-safe-route-dialog.component.scss']
})
export class SaveSafeRouteDialogComponent {
    public selectedCameraIds: string[] = [];
    public selectedColor: string = '#F44336';
    public selectedName: string = '';
    public deviationDistanceMeters: number | null = null;
    public distanceToleranceValue: number | null = null;
    public distanceUnit: string = 'km';
    public readonly colorOptions: Array<{ value: string; label: string }> = [
        { value: '#4CAF50', label: 'Green' },
        { value: '#1976D2', label: 'Blue' },
        { value: '#FF9800', label: 'Orange' },
        { value: '#F44336', label: 'Red' },
        { value: '#9C27B0', label: 'Purple' },
        { value: '#000000', label: 'Black' }
    ];

    constructor(
        public dialogRef: MatDialogRef<SaveSafeRouteDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: SaveSafeRouteDialogData,
        public translate: TranslateService
    ) {
        console.log('Dialog data:', data);
        this.selectedCameraIds = [...(data?.selectedCameraIds || [])];
        this.selectedColor = this.resolveSelectedColor(data?.selectedColor);
        this.selectedName = String(data?.selectedName || '');
        this.distanceUnit = this.resolveDistanceUnit(data?.distanceUnit);
        this.deviationDistanceMeters = this.normalizeDeviationDistance(data?.selectedDeviationDistanceMeters) ?? null;
        this.distanceToleranceValue = this.fromMeters(this.deviationDistanceMeters);
    }

    confirm() {
        const cameraIds: SaveSafeRouteCameraRef[] = (this.selectedCameraIds || [])
            .map((id) => {
                const camera = (this.data?.cameras || []).find((cam: any) => String(cam?._id) === String(id));
                return {
                    _id: String(id),
                    name: camera?.name ?? null
                };
            })
            .filter((item) => item._id.length > 0);

        const result: SaveSafeRouteDialogResult = {
            cameraIds,
            color: this.selectedColor || '#F44336',
            name: this.selectedName.trim(),
            deviationDistanceMeters: this.toMeters(this.distanceToleranceValue)
        };

        this.dialogRef.close(result);
    }

    cancel() {
        this.dialogRef.close();
    }

    private resolveSelectedColor(value?: string): string {
        const fallback = '#F44336';
        const raw = String(value || '').trim().toUpperCase();
        if (!raw) {
            return fallback;
        }
        const exact = this.colorOptions.find((option) => String(option.value || '').trim().toUpperCase() === raw);
        if (exact) {
            return exact.value;
        }
        // Legacy/default draft red used in map pages; map it to dropdown's Red option.
        if (raw === '#FF3B30') {
            return fallback;
        }
        return fallback;
    }

    isDisabled(cam: any): boolean {
        return cam.hasSafeRoute && !cam.routes?.some(r => r._id === this.data.currentRouteId);
    }

    private normalizeDeviationDistance(value: any): number | undefined {
        if (value === null || value === undefined || value === '') {
            return undefined;
        }
        const distance = Number(value);
        if (!Number.isFinite(distance) || distance < 0) {
            return undefined;
        }
        return distance;
    }

    private resolveDistanceUnit(value?: string): string {
        return String(value || '').toLowerCase() === 'miles' ? 'miles' : 'km';
    }

    private fromMeters(value: number | null): number | null {
        if (value === null || value === undefined) {
            return null;
        }
        return this.distanceUnit === 'miles'
            ? this.roundDistance(value / 1609.344)
            : this.roundDistance(value / 1000);
    }

    private toMeters(value: any): number | undefined {
        const normalizedValue = this.normalizeDeviationDistance(value);
        if (normalizedValue === undefined) {
            return undefined;
        }
        const meters = this.distanceUnit === 'miles'
            ? normalizedValue * 1609.344
            : normalizedValue * 1000;
        return Math.round(meters);
    }

    private roundDistance(value: number): number {
        return Math.round(value * 1000) / 1000;
    }
}
